import React, { createContext, useContext, useEffect, useState } from 'react';
import { usePWA } from '../hooks/usePWA';
import { useNotifications } from '../hooks/useNotifications';

/**
 * Contexte PWA global pour Cabinet 4.0
 * Centralise la gestion de la PWA, des mises à jour et des notifications
 */

// ============================================
// CONTEXTE
// ============================================

const PWAContext = createContext(null);

// ============================================
// PROVIDER
// ============================================

export function PWAProvider({ children }) {
  const pwa = usePWA();
  const notifications = useNotifications();
  
  // État pour les bannières et modales
  const [showUpdateBanner, setShowUpdateBanner] = useState(false);
  const [showInstallBanner, setShowInstallBanner] = useState(false);
  const [installBannerDismissed, setInstallBannerDismissed] = useState(() => {
    return localStorage.getItem('pwa-install-dismissed') === 'true';
  });

  // ============================================
  // GESTION DES MISES À JOUR
  // ============================================

  useEffect(() => {
    if (pwa.updateAvailable) {
      setShowUpdateBanner(true);
    }
  }, [pwa.updateAvailable]);

  const handleUpdate = async () => {
    const success = await pwa.applyUpdate();
    if (success) {
      setShowUpdateBanner(false);
    }
  };

  const dismissUpdateBanner = () => {
    setShowUpdateBanner(false);
  };

  // ============================================
  // GESTION DE L'INSTALLATION
  // ============================================

  useEffect(() => {
    // Afficher la bannière d'installation après 5s si pas installé et pas déjà refusé
    if (pwa.isInstallable && !pwa.isInstalled && !installBannerDismissed) {
      const timer = setTimeout(() => {
        setShowInstallBanner(true);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [pwa.isInstallable, pwa.isInstalled, installBannerDismissed]);

  const handleInstall = async () => {
    const result = await pwa.installPWA();
    if (result.success) {
      setShowInstallBanner(false);
    }
    return result;
  };

  const dismissInstallBanner = (permanent = false) => {
    setShowInstallBanner(false);
    if (permanent) {
      setInstallBannerDismissed(true);
      localStorage.setItem('pwa-install-dismissed', 'true');
    }
  };

  // ============================================
  // GESTION DES NOTIFICATIONS
  // ============================================

  const enableNotifications = async () => {
    const sub = await notifications.subscribe();
    return !!sub;
  };

  const disableNotifications = async () => {
    return await notifications.unsubscribe();
  };

  // ============================================
  // ÉCOUTE DES CLICS DE NOTIFICATION
  // ============================================

  useEffect(() => {
    const handleNotificationClick = (event) => {
      console.log('[PWA] Notification cliquée:', event.detail);
      // Navigation gérée par le composant parent ou le router
    };

    window.addEventListener('pwa-notification-click', handleNotificationClick);
    return () => {
      window.removeEventListener('pwa-notification-click', handleNotificationClick);
    };
  }, []);

  // ============================================
  // VALEUR DU CONTEXTE
  // ============================================

  const value = {
    // États PWA
    isInstalled: pwa.isInstalled,
    isInstallable: pwa.isInstallable,
    isOnline: pwa.isOnline,
    isStandalone: pwa.isStandalone,
    isMobile: pwa.isMobile,
    isIOS: pwa.isIOS,
    isAndroid: pwa.isAndroid,
    swVersion: pwa.swVersion,

    // États Notifications
    notificationPermission: notifications.permission,
    isNotificationsEnabled: notifications.isSubscribed,
    notificationPreferences: notifications.preferences,

    // États UI
    showUpdateBanner,
    showInstallBanner,
    updateAvailable: pwa.updateAvailable,

    // Actions PWA
    installPWA: handleInstall,
    dismissInstallBanner,
    applyUpdate: handleUpdate,
    dismissUpdateBanner,
    clearCache: pwa.clearCache,
    checkForUpdates: pwa.checkForUpdates,

    // Actions Notifications
    enableNotifications,
    disableNotifications,
    requestNotificationPermission: notifications.requestPermission,
    updateNotificationPreferences: notifications.updatePreferences,
    
    // Notifications spécialisées
    notifyAudience: notifications.notifyAudience,
    notifyEcheance: notifications.notifyEcheance,
    notifyRappel: notifications.notifyRappel,
    showLocalNotification: notifications.showLocalNotification,

    // Support
    supportsPush: pwa.supportsPush,
    supportsSync: pwa.supportsSync,
    supportsPeriodicSync: pwa.supportsPeriodicSync,
    isNotificationsSupported: notifications.isSupported
  };

  return (
    <PWAContext.Provider value={value}>
      {children}
    </PWAContext.Provider>
  );
}

// ============================================
// HOOK D'UTILISATION
// ============================================

export function usePWAContext() {
  const context = useContext(PWAContext);
  if (!context) {
    throw new Error('usePWAContext doit être utilisé dans un PWAProvider');
  }
  return context;
}

export default PWAContext;
